import React, { useState } from 'react';
import {
	Button,
	Box,
	Typography,
	CircularProgress,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	SelectChangeEvent,
} from '@mui/material';
import { ToyotaFleetRegistration } from '../types';
import api from '../../../utils/api';

interface ToyotaFleetRegistrationProps {
	vehicleId: number;
	registration?: ToyotaFleetRegistration;
	onUpdate: () => void;
}

const ToyotaFleetRegistrationComponent: React.FC<ToyotaFleetRegistrationProps> = ({
	vehicleId,
	registration,
	onUpdate,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [showRegisterDialog, setShowRegisterDialog] = useState(false);
	const [expirationDays, setExpirationDays] = useState<number | null>(30);

	const handleRegister = async () => {
		setLoading(true);
		setError(null);
		setSuccess(null);

		try {
			const response = await api.post(`/vehicles/${vehicleId}/register-toyota-fleet/`, {
				expiration_days: expirationDays,
			});

			if (response.error) {
				setError((response.error as any)?.message || response.error || 'Failed to de-activate telematics API');
			} else {
				setSuccess('Telematics API de-activated successfully!');
				setShowRegisterDialog(false);
				onUpdate();
			}
		} catch (err) {
			setError('Failed to de-activate telematics API. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleUnregister = async () => {
		setLoading(true);
		setError(null);
		setSuccess(null);

		try {
			const response = await api.post(`/vehicles/${vehicleId}/unregister-toyota-fleet/`);

			if (response.error) {
				setError((response.error as any)?.message || response.error || 'Failed to re-activate telematics API');
			} else {
				setSuccess('Telematics API re-activated successfully!');
				onUpdate();
			}
		} catch (err) {
			setError('Failed to re-activate telematics API. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const getStatusText = (daysRemaining: number | null) => {
		if (daysRemaining === null) return 'De-activated forever';
		if (daysRemaining <= 0) return 'Expired';
		if (daysRemaining === 1) return '1 day remaining';
		return `${daysRemaining} days remaining`;
	};

	return (
		<Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
			<Typography variant="body1" gutterBottom>
				Manually deactivate OEM data for geolocation This action can be undone.
			</Typography>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{success && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{success}
				</Alert>
			)}

			{registration?.isRegistered ? (
				<Box>
					<Typography
						variant="body1"
						sx={{ mb: 2 }}
					>
						<b>
							Telematics API de-activated, {getStatusText(registration.daysRemaining)}
						</b>
					</Typography>
					<Button
						variant="outlined"
						onClick={handleUnregister}
						disabled={loading}
						startIcon={loading ? <CircularProgress size={16} /> : null}
					>
						{loading ? 'Re-activating...' : 'Re-activate Telematics API'}
					</Button>
				</Box>
			) : (
				<Box>
					<Button
						variant="contained"
						color="primary"
						onClick={() => setShowRegisterDialog(true)}
						disabled={loading}
					>
						De-activate Telematics API
					</Button>
				</Box>
			)}

			{/* Register Dialog */}
			<Dialog open={showRegisterDialog} onClose={() => setShowRegisterDialog(false)}>
				<DialogTitle>De-activate Telematics API</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Select the duration to de-activate telematics API:
					</Typography>
					<FormControl fullWidth>
						<InputLabel>API De-activated Duration</InputLabel>
						<Select
							value={expirationDays === null ? 'forever' : `${expirationDays}`}
							label="API De-activated Duration"
							onChange={(e: SelectChangeEvent) => {
								const value = e.target.value;
								setExpirationDays(value === 'forever' ? null : Number(value));
							}}
						>
							<MenuItem value={7}>7 days</MenuItem>
							<MenuItem value={14}>14 days</MenuItem>
							<MenuItem value={30}>30 days</MenuItem>
							<MenuItem value={60}>60 days</MenuItem>
							<MenuItem value={90}>90 days</MenuItem>
							<MenuItem value="forever">Forever</MenuItem>
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowRegisterDialog(false)}>Cancel</Button>
					<Button
						onClick={handleRegister}
						variant="contained"
						disabled={loading}
						startIcon={loading ? <CircularProgress size={16} /> : null}
					>
						{loading ? 'De-activating...' : 'De-activate API'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ToyotaFleetRegistrationComponent;